import { useState } from "react";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../config/stripe";
import RandomQuote from "../../components/RandomQuote/RandomQuote";
import Notification from "../../components/Notification/Notification";
import Spinner from "../../components/Spinner/Spinner";
import BrandTitle from "../../components/BrandTitle/BrandTitle";
import PaymentForm from "../../components/PaymentForm/PaymentForm";
import { createSetupIntentForSignup } from "../../api/stripe/stripe";

function SignupForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [step, setStep] = useState(1);
  const [accountType, setAccountType] = useState("regular"); // "regular" or "enterprise"
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [cardholderName, setCardholderName] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [billingContact, setBillingContact] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBillingChange = (e) => {
    setBillingContact({ ...billingContact, [e.target.name]: e.target.value });
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError("");
    }
  };

  const goToNextStep = (e) => {
    e.preventDefault();
    setError("");
    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      setError("Please fill all required fields");
      return;
    }
    if (form.password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    // Pre-fill billing contact email with user email for enterprise
    if (accountType === "enterprise" && !billingContact.email) {
      setBillingContact({ ...billingContact, email: form.email });
    }
    setStep(2);
  };

  const handleBack = (e) => {
    e.preventDefault();
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!termsAccepted) {
      setError(
        "Please accept the Terms of Service and Privacy Policy to continue",
      );
      return;
    }

    setIsLoading(true);

    try {
      if (accountType === "enterprise") {
        // Enterprise signup - no payment required
        if (
          !billingContact.name ||
          !billingContact.email ||
          !billingContact.company
        ) {
          setError("Please fill all billing contact fields");
          setIsLoading(false);
          return;
        }

        const payload = {
          ...form,
          billingContact,
        };

        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/auth/signup-enterprise`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Signup failed");

        setSuccessMessage(
          "Enterprise account created! Check your email to verify your account. The link in the email will lead you back to login.",
        );
        setNotification({
          message: "Enterprise account created! Check your email to verify.",
          type: "success",
        });
      } else {
        // Regular signup - requires payment
        if (!stripe || !elements) {
          setError("Stripe is not ready. Please try again.");
          setIsLoading(false);
          return;
        }

        if (!cardComplete) {
          setError("Please complete your card details");
          setIsLoading(false);
          return;
        }

        // Step 1: Create SetupIntent for 3D Secure authentication
        console.log("[Signup] Creating SetupIntent...");
        const { clientSecret } = await createSetupIntentForSignup(
          form.email,
          `${form.firstName} ${form.lastName}`,
        );
        console.log("[Signup] SetupIntent created, confirming card setup...");

        // Step 2: Confirm card setup (triggers 3D Secure/MitID if needed)
        const cardElement = elements.getElement(CardNumberElement);
        const { error: stripeError, setupIntent } =
          await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: cardholderName,
                email: form.email,
              },
            },
          });

        if (stripeError) {
          console.error("[Signup] Card setup failed:", stripeError.message);
          throw new Error(stripeError.message);
        }

        console.log(
          "[Signup] Card setup successful, payment method:",
          setupIntent.payment_method,
        );

        // Step 3: Send signup request with authenticated payment method
        const payload = {
          ...form,
          payment: {
            paymentMethodId: setupIntent.payment_method,
            cardName: cardholderName,
          },
        };

        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/auth/signup`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Signup failed");

        setSuccessMessage(
          "Account created! Check your email to verify your account. The link in the email will lead you back to login.",
        );
        setNotification({
          message: "Account created! Check your email to verify.",
          type: "success",
        });
      }

      // Don't redirect - let them read the message and close the tab
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="authentication-view">
      {isLoading && (
        <Spinner
          text={
            accountType === "enterprise"
              ? "... creating your enterprise account"
              : "... creating your account"
          }
        />
      )}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
      <div className="left-wrapper">
        <div className="title">
          <BrandTitle fontSize={40} className="brand-title-login" />
        </div>
        <div className="quote">
          <RandomQuote />
        </div>
      </div>

      <div className="right-wrapper">
        {successMessage ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: "40px",
              maxWidth: "500px",
              height: "100%",
              margin: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  color: accountType === "enterprise" ? "#6c5ce7" : "#10b981",
                }}
              >
                ✓
              </div>
              <div
                className="title"
                style={{
                  margin: 0,
                  color: accountType === "enterprise" ? "#6c5ce7" : "#10b981",
                }}
              >
                Success!
              </div>
            </div>
            {accountType === "enterprise" && (
              <div
                style={{
                  display: "inline-block",
                  padding: "6px 12px",
                  background: "rgba(108, 92, 231, 0.1)",
                  color: "#6c5ce7",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "20px",
                }}
              >
                Enterprise Account
              </div>
            )}
            <p
              style={{
                fontSize: "16px",
                lineHeight: "1.6",
                color: "#ccc",
                marginBottom: "30px",
              }}
            >
              {successMessage}
            </p>
            <p>You can close this window now.</p>

            {!successMessage && (
              <p>
                Already have an account?{" "}
                <a className="link" href="/login">
                  Log in
                </a>
              </p>
            )}

            {successMessage && (
              <p style={{ marginTop: "40px" }}>
                <a className="link" href="/login">
                  Home
                </a>
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="title">
              {step === 1
                ? "Sign Up"
                : accountType === "enterprise"
                  ? "Billing Contact"
                  : "Payment Info"}
            </div>

            {step === 1 && (
              <form onSubmit={goToNextStep} autoComplete="off">
                {/* Account Type Selection */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "12px",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "var(--main-text-color)",
                    }}
                  >
                    Account Type
                  </label>
                  <div style={{ display: "flex", gap: "16px" }}>
                    <label
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        border: `2px solid ${accountType === "regular" ? "var(--blue-button-color)" : "var(--border-color)"}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        background:
                          accountType === "regular"
                            ? "rgba(35, 130, 228, 0.1)"
                            : "transparent",
                      }}
                    >
                      <input
                        type="radio"
                        name="accountType"
                        value="regular"
                        checked={accountType === "regular"}
                        onChange={(e) => setAccountType(e.target.value)}
                        style={{ marginRight: "8px" }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        Regular
                      </span>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--secondary-text-color)",
                          marginTop: "4px",
                          marginLeft: "24px",
                        }}
                      >
                        Subscription billing
                      </div>
                    </label>
                    <label
                      style={{
                        flex: 1,
                        padding: "12px 16px",
                        border: `2px solid ${accountType === "enterprise" ? "#6c5ce7" : "var(--border-color)"}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        background:
                          accountType === "enterprise"
                            ? "rgba(108, 92, 231, 0.1)"
                            : "transparent",
                      }}
                    >
                      <input
                        type="radio"
                        name="accountType"
                        value="enterprise"
                        checked={accountType === "enterprise"}
                        onChange={(e) => setAccountType(e.target.value)}
                        style={{ marginRight: "8px" }}
                      />
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        Enterprise
                      </span>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "var(--secondary-text-color)",
                          marginTop: "4px",
                          marginLeft: "24px",
                        }}
                      >
                        Invoice billing
                      </div>
                    </label>
                  </div>
                </div>

                <div className="auth-names">
                  <input
                    name="firstName"
                    className="flow-form-row"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    autoComplete="new-first-name"
                    required
                  />
                  <input
                    name="lastName"
                    className="flow-form-row"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    autoComplete="new-last-name"
                    required
                  />
                </div>
                <br />
                <input
                  name="email"
                  className="flow-form"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Email"
                  autoComplete="new-email"
                  required
                />
                <br />
                <input
                  name="password"
                  className="flow-form"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Password"
                  autoComplete="new-password"
                  required
                />
                <br />
                <input
                  name="confirmPassword"
                  className="flow-form"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm Password"
                  autoComplete="new-confirm-password"
                  required
                />
                <br />
                {error && step === 1 && (
                  <span
                    style={{
                      color: "var(--error-color)",
                      fontSize: "14px",
                      display: "block",
                      marginBottom: "10px",
                    }}
                  >
                    {error}
                  </span>
                )}
                <button type="submit" className="flow-button">
                  {accountType === "enterprise"
                    ? "Next: Billing Contact"
                    : "Next: Payment"}
                </button>
              </form>
            )}

            {step === 2 && accountType === "regular" && (
              <form
                onSubmit={handleSubmit}
                autoComplete="off"
                style={{ maxWidth: "440px" }}
              >
                <input
                  name="cardholderName"
                  className="flow-form"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value)}
                  placeholder="Cardholder Name"
                />
                <br />

                <PaymentForm onCardChange={handleCardChange} />
                <br />

                {/* Billing explanation and terms */}
                <div
                  style={{
                    marginTop: "24px",
                    marginBottom: "20px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#999",
                    maxWidth: "430px",
                  }}
                >
                  <p style={{ margin: "0 0 16px 0" }}>
                    You only pay for organizations you create and members you
                    invite. If you're invited to join another organization,
                    there's no charge.
                  </p>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        marginTop: "3px",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      I agree to the{" "}
                      <a
                        href="/legal/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#6366f1",
                          textDecoration: "underline",
                        }}
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/legal/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#6366f1",
                          textDecoration: "underline",
                        }}
                      >
                        Privacy Policy
                      </a>
                      , and understand the billing terms to start a 14-day free
                      trial.
                    </span>
                  </label>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "430px",
                  }}
                >
                  <button
                    onClick={handleBack}
                    className="flow-button"
                    style={{
                      background: "#ddd",
                      color: "#000",
                      width: "190px",
                      height: "60px",
                      padding: "10px",
                      boxSizing: "border-box",
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="flow-button"
                    disabled={!termsAccepted}
                    style={{
                      width: "190px",
                      height: "60px",
                      padding: "10px",
                      boxSizing: "border-box",
                      opacity: termsAccepted ? 1 : 0.5,
                      cursor: termsAccepted ? "pointer" : "not-allowed",
                    }}
                  >
                    Create Account
                  </button>
                </div>
              </form>
            )}

            {step === 2 && accountType === "enterprise" && (
              <form onSubmit={handleSubmit} autoComplete="off">
                <div
                  style={{
                    marginBottom: "20px",
                    fontSize: "14px",
                    color: "#999",
                  }}
                >
                  <p style={{ margin: "0 0 10px 0" }}>
                    <strong>Invoice Billing Information</strong>
                  </p>
                  <p style={{ margin: 0 }}>
                    Invoices will be sent to this contact each month.
                  </p>
                </div>

                <input
                  name="company"
                  className="flow-form"
                  value={billingContact.company}
                  onChange={handleBillingChange}
                  placeholder="Company/Organization Name *"
                  required
                />
                <br />
                <input
                  name="name"
                  className="flow-form"
                  value={billingContact.name}
                  onChange={handleBillingChange}
                  placeholder="Billing Contact Name *"
                  required
                />
                <br />
                <input
                  name="email"
                  className="flow-form"
                  type="email"
                  value={billingContact.email}
                  onChange={handleBillingChange}
                  placeholder="Billing Contact Email *"
                  required
                />
                <br />
                <input
                  name="phone"
                  className="flow-form"
                  type="tel"
                  value={billingContact.phone}
                  onChange={handleBillingChange}
                  placeholder="Billing Contact Phone (Optional)"
                />
                <br />

                {/* Terms and conditions */}
                <div
                  style={{
                    marginTop: "24px",
                    marginBottom: "20px",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#999",
                  }}
                >
                  <p style={{ margin: "0 0 16px 0" }}>
                    <strong>Enterprise Billing:</strong> You'll be invoiced
                    monthly based on active members in your organization at $8
                    per member. Invoices are sent with 30-day payment terms.
                  </p>

                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        marginTop: "3px",
                        cursor: "pointer",
                        width: "16px",
                        height: "16px",
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      I agree to the{" "}
                      <a
                        href="/legal/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#6c5ce7",
                          textDecoration: "underline",
                        }}
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/legal/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#6c5ce7",
                          textDecoration: "underline",
                        }}
                      >
                        Privacy Policy
                      </a>
                      , and understand the enterprise billing terms to start a
                      14-day free trial.
                    </span>
                  </label>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flow-button"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--border-color)",
                      color: "var(--main-text-color)",
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="flow-button"
                    disabled={!termsAccepted}
                    style={{
                      flex: 1,
                      background: "#6c5ce7",
                      opacity: termsAccepted ? 1 : 0.5,
                      cursor: termsAccepted ? "pointer" : "not-allowed",
                    }}
                  >
                    Create Enterprise Account
                  </button>
                </div>
              </form>
            )}

            {error && step === 2 && (
              <span
                style={{
                  color: "var(--error-color)",
                  marginTop: "10px",
                  marginBottom: "-10px",
                  display: "block",
                }}
              >
                {error}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Wrap SignupForm with Stripe Elements provider
export default function SignupPage() {
  console.log("[SignupPage] stripePromise:", stripePromise);

  return (
    <Elements stripe={stripePromise}>
      <SignupForm />
    </Elements>
  );
}
